import { AxiosHttpAuthClient } from '../axios-http-auth-client'
import {
  ApiInstanceRequest,
  ApiInstanceResponse,
  ApiInstanceReturn,
  ErrorFactory
} from './http-service-client-types'
import { HttpStatusCode } from '@/data/protocols/http'
import {
  InvalidCredentialsError,
  ServerError,
  ForbiddenError,
  NotFoundError,
  ConflictError,
  BadRequestError
} from '@/domain/errors'
import { clearAuthData, redirectToLogin } from '@/data/services/login/login-service-utils'

const errorMap: Record<number, ErrorFactory> = {
  [HttpStatusCode.badRequest]: msg => new BadRequestError(msg),
  [HttpStatusCode.forbidden]: msg => new ForbiddenError(msg),
  [HttpStatusCode.unauthorized]: msg => new InvalidCredentialsError(msg),
  [HttpStatusCode.notFound]: msg => new NotFoundError(msg),
  [HttpStatusCode.conflict]: msg => new ConflictError(msg),
  [HttpStatusCode.serverError]: msg => new ServerError(msg)
}

/**
 * Handles errors based on the API response status code and body.
 *
 * - If the response status code matches an entry in `errorMap`, throws the corresponding error with the response message.
 * - If the response status code indicates success but the response body indicates failure, throws a bad request error.
 * - Otherwise, does nothing.
 *
 * @template T The type of the response body.
 * @param response The API response object containing status code and body.
 * @throws Throws an error if the response status code or body indicates a failure.
 */
const handleError = <T>(response: ApiInstanceReturn<T>): never | void => {
  const errorFactory = errorMap[response.statusCode]
  if (errorFactory) {
    throw Object.assign(errorFactory(response.body?.message), {
      statusCode: response.statusCode,
      response: {
        data: response.body
      }
    })
  }

  const isSuccessStatus =
    response.statusCode >= HttpStatusCode.ok && response.statusCode < HttpStatusCode.multipleChoices

  // If status is success but body indicates failure, throw BadRequestError
  const bodyIsFailure =
    response.body == null || (typeof response.body === 'object' && response.body.success === false)

  if (isSuccessStatus && bodyIsFailure) {
    throw Object.assign(errorMap[HttpStatusCode.badRequest](response.body?.message), {
      statusCode: response.statusCode,
      response: {
        data: response.body
      }
    })
  }
}

/**
 * Creates an instance of `AxiosHttpAuthClient` configured to handle authentication redirects.
 *
 * When authentication is required (401 Unauthorized), the client will:
 * 1. Clear all authentication data (cookies, sessionStorage)
 * 2. Redirect to internal login page
 *
 * @param skipAuthRedirect - If true, skip automatic redirect to login on 401 errors
 * @returns {AxiosHttpAuthClient} An authenticated HTTP client for making requests.
 */
const createAuthClient = (skipAuthRedirect = false): AxiosHttpAuthClient => {
  return new AxiosHttpAuthClient(() => {
    console.log('[HttpServiceClient] ❌ 401 Unauthorized, clearing auth and redirecting to login')
    clearAuthData()
    redirectToLogin(window.location.href)
  }, skipAuthRedirect)
}

/**
 * A record to keep track of active AbortControllers for each URL.
 * This allows for aborting previous requests to the same URL when a new request is made.
 */
const controllers = new Map<string, AbortController>()

/**
 * Makes an HTTP request using an authenticated Axios client and returns a standardized response.
 *
 * @template T - The expected type of the response body.
 * @param {ApiInstanceRequest} config - The configuration object for the HTTP request.
 * @returns {Promise<ApiInstanceReturn<T>>} - A promise that resolves to the standardized API response.
 *
 * @remarks
 * This function creates an authenticated Axios client, performs the request, handles any errors,
 * and returns the response in a consistent format.
 */
export async function httpServiceClient<T>(
  config: ApiInstanceRequest
): Promise<ApiInstanceReturn<T>> {
  const axiosHttpAuthClient = createAuthClient(config.skipAuthRedirect)

  // Use the provided signal if available (from React Query), otherwise use our own AbortController
  let signal: AbortSignal

  if (config.signal) {
    signal = config.signal
  } else {
    const abortController = new AbortController()

    if (controllers.has(config.url)) {
      controllers.get(config.url)!.abort()
    }

    controllers.set(config.url, abortController)
    signal = abortController.signal
  }

  const apiResponse = await axiosHttpAuthClient.request({
    ...config,
    signal
  })

  const isStandardResponse =
    apiResponse.body != null && typeof apiResponse.body === 'object' && 'data' in apiResponse.body

  const body: ApiInstanceResponse<T> = isStandardResponse
    ? (apiResponse.body as ApiInstanceResponse<T>)
    : { success: true, data: apiResponse.body as T }

  const response: ApiInstanceReturn<T> = {
    statusCode: apiResponse.statusCode,
    headers: apiResponse.headers,
    body
  }

  handleError(response)

  return response
}
